// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * React hook module for managing return order lists with filtering capabilities.
 * Provides a convenient interface for fetching and filtering return order data.
 */
import { locationCompanyTypeSchema } from '@/constants/location-company-type';
import { sortDirectionSchema } from '@/constants/sort';
import { getReturnOrders } from '@/return-order/api/get-return-orders';
import { returnOrderReasonSchema } from '@/return-order/api/types';
import {
  ReturnOrderFilter,
  roPhaseSchema,
  roSubstatusSchema,
} from '@/return-order/constants/status';
import { returnOrderListQueryKey } from '@/return-order/query-keys';
import { searchTermSchema } from '@/utilities/search-term';
import { useSuspenseQuery } from '@tanstack/react-query';
import { z } from 'zod';

/**
 * Configuration options for the useReturnOrders hook.
 */
interface UseReturnOrdersOptions {
  filter?: ReturnOrderFilter;
}

/**
 * React hook for fetching and managing return order lists with filtering.
 * Uses React Query's suspense mode for data fetching.
 *
 * Features:
 * - Automatic data fetching and caching via React Query
 * - Filtering support via ReturnOrderFilter options
 * - Suspense mode for loading states
 *
 * @param options - Configuration object for the hook
 *
 * @returns Object containing:
 * - returnOrders: Filtered array of return orders
 * - resultCount: Total count of results
 * - Query status information (error, refetch, etc.)
 *
 * @throws Will suspend while loading data
 * @throws Will throw errors from the API if request fails
 */
export function useReturnOrders(options: UseReturnOrdersOptions = {}) {
  // Set up React Query with suspense mode
  const { data, ...query } = useSuspenseQuery({
    queryKey: returnOrderListQueryKey(options.filter),
    queryFn: () => getReturnOrders(options.filter),
    refetchOnWindowFocus: true,
  });

  const returnOrders = data.returnOrders;
  const resultCount = data.resultCount;
  return { returnOrders, resultCount, ...query };
}

/**
 * Sort property options for return orders
 */
const returnOrderSortPropertySchema = z.enum(['roNumber', 'status']);

export type ReturnOrderSortProperty = z.infer<
  typeof returnOrderSortPropertySchema
>;

/**
 * Search schema for return order listing page.
 *
 * Filter dimensions: status, sub-statuses, reason, search query, and
 * from/to location filters. Each location filter (from and to) supports
 * three independent dimensions: explicit location IDs, customer IDs (which
 * select all of that customer's locations), and location company types
 * (which select all manufacturer or customer locations as a group).
 */
export const returnOrderSearchListingSchema = z.object({
  query: searchTermSchema,
  status: roPhaseSchema.optional(),
  subStatuses: z.array(roSubstatusSchema).optional(),
  // Uses returnOrderReasonSchema from types.ts as the single source of truth
  // for reason values (avoids duplication with the API layer)
  reason: returnOrderReasonSchema.optional(),
  page: z.number().optional(),
  sort: returnOrderSortPropertySchema.optional(),
  direction: sortDirectionSchema.optional(),
  fromLocations: z.array(z.string()).optional(),
  fromCustomerLocations: z.array(z.string()).optional(),
  fromLocationCompanyTypes: z
    .array(locationCompanyTypeSchema)
    .optional()
    .catch([]),
  toLocations: z.array(z.string()).optional(),
  toCustomerLocations: z.array(z.string()).optional(),
  toLocationCompanyTypes: z
    .array(locationCompanyTypeSchema)
    .optional()
    .catch([]),
});

export type ReturnOrderSearchListing = z.infer<
  typeof returnOrderSearchListingSchema
>;
